import React, {useEffect, useState} from 'react'
import {Box, Card, CardContent, Grid, Typography} from '@mui/material'
import {SaveInventoryItem} from '../utils'
import {CachedProduct} from '../productCache'
import {ExpectedNonSerials, ExpectedSerials} from '../transferUtils'
import ReceiveSummaryTable from './ReceiveSummaryTable'

interface CountIterationReportProps {
    serialItems: SaveInventoryItem[]
    nonSerialItems: SaveInventoryItem[]
    expectedSerialItems: ExpectedSerials[]
    expectedNonSerialItems: ExpectedNonSerials[]
    products: CachedProduct[]
}

type SerialRows = {
    serial: string | null,
    expected: number,
    received: number,
    unexpected: number,
    missing: number
}
type NonSerialRows = {
    sku: string | null,
    expected: number,
    received: number,
    unexpected: number,
    missing: number
}

type SummaryData = {
    saved: number,
    unexpected: number,
    missing: number
}

const ReceiveSummary = ({
                            serialItems,
                            nonSerialItems,
                            expectedSerialItems,
                            expectedNonSerialItems,
                            products
                        }: CountIterationReportProps) => {
    //const [products, setProducts] = useState<CachedProduct[]>([])
    const [serialReceivedRows, setSerialReceivedRows] = useState<SerialRows[]>([])
    const [nonSerialReceivedRows, setNonSerialReceivedRows] = useState<NonSerialRows[]>([])
    const [serialsSummaryData, setSerialsSummaryData] = useState<SummaryData>({
        saved: 0,
        unexpected: 0,
        missing: 0
    })
    const [nonSerialsSummaryData, setNonSerialsSummaryData] = useState<SummaryData>({
        saved: 0,
        unexpected: 0,
        missing: 0
    })
    useEffect(() => {
        prepSummaryRows()
    }, [])

    const reportSerialItems = [...serialItems].reverse()
    const reportNonSerialItems = [...nonSerialItems]

    //count total unexpected for serials nonserials
    const prepSummaryRows = () => {
        //Serials totals
        let receivedCorrectSerialQtyRows: SerialRows[] = []
        let receivedSerialTotal = 0
        let unExpectedSerialTotal = 0
        let missingSerialTotal = 0

        //Non Serial Totals
        let receivedCorrectNonSerialQtyRows: NonSerialRows[] = []
        let receivedNonSerialTotal = 0
        let unExpectedNonSerialTotal = 0
        let missingNonSerialTotal = 0

        reportSerialItems.forEach(item => {
            const foundinExpectedSerials = expectedSerialItems.find(x => x.serial === item.serial)
            if (!foundinExpectedSerials) { //not expected
                receivedCorrectSerialQtyRows.push({
                    serial: item.serial,
                    expected: 0,
                    received: item.quantity,
                    unexpected: item.quantity,
                    missing: 0,
                })
                unExpectedSerialTotal += item.quantity
            } else { //expected item
                const overflowAmt = item.quantity - foundinExpectedSerials.quantity
                const rowItem = {
                    serial: item.serial,
                    expected: foundinExpectedSerials.quantity,
                    received: item.quantity,
                    unexpected: overflowAmt > 0 ? overflowAmt : 0,
                    missing: overflowAmt < 0 ? Math.abs(overflowAmt) : 0 ,
                }
                receivedCorrectSerialQtyRows.push(rowItem)
                if (foundinExpectedSerials.quantity === item.quantity) {
                    receivedSerialTotal += item.quantity
                } else if (overflowAmt < 0) { //missing items, receieved less < expected
                    missingSerialTotal += Math.abs(overflowAmt)
                }
            }
        })

        expectedSerialItems.forEach(expectedItem => {
            const foundInReport = reportSerialItems.some(item => item.serial === expectedItem.serial);

            if (!foundInReport) {
                // This serial was expected but not received in the report
                receivedCorrectSerialQtyRows.push({
                    serial: expectedItem.serial,
                    expected: expectedItem.quantity,
                    received: 0,
                    unexpected: 0,
                    missing: expectedItem.quantity,
                })
                missingSerialTotal += expectedItem.quantity
            }
        })

        reportNonSerialItems.forEach(item => {
            const foundinExpectedNonSerials = expectedNonSerialItems.find(x => x.sku === item.sku)
            if (!foundinExpectedNonSerials) { //not expected
                receivedCorrectNonSerialQtyRows.push({
                    sku: item.sku,
                    expected: 0,
                    received: item.quantity,
                    unexpected: item.quantity,
                    missing: 0,
                })
                unExpectedNonSerialTotal += item.quantity
            } else { //expected item
                const overflowAmt = item.quantity - foundinExpectedNonSerials.quantity
                const rowItem = {
                    sku: item.sku,
                    expected: foundinExpectedNonSerials.quantity,
                    received: item.quantity,
                    unexpected: overflowAmt > 0 ? overflowAmt : 0,
                    missing: overflowAmt < 0 ? Math.abs(overflowAmt) : 0
                }
                receivedCorrectNonSerialQtyRows.push(rowItem)
                if (foundinExpectedNonSerials.quantity === item.quantity) {
                    receivedNonSerialTotal += item.quantity
                } else if (overflowAmt > 0) { //expected item got more items than expected
                    unExpectedNonSerialTotal += overflowAmt
                    receivedNonSerialTotal += foundinExpectedNonSerials.quantity
                } else if (overflowAmt < 0) { //missing items
                    missingNonSerialTotal += Math.abs(overflowAmt)
                }
            }
        })
        expectedNonSerialItems.forEach(expectedItem => {
            const foundInReport = reportNonSerialItems.some(item => item.sku === expectedItem.sku);

            if (!foundInReport) {
                // This serial was expected but not received in the report
                receivedCorrectNonSerialQtyRows.push({
                    sku: expectedItem.sku,
                    expected: expectedItem.quantity,
                    received: 0,
                    unexpected: 0,
                    missing: expectedItem.quantity,
                })
                missingSerialTotal += expectedItem.quantity
            }
        })

        setSerialReceivedRows(receivedCorrectSerialQtyRows)
        setNonSerialReceivedRows(receivedCorrectNonSerialQtyRows)
        setSerialsSummaryData({
            saved: receivedSerialTotal,
            unexpected: unExpectedSerialTotal,
            missing: missingSerialTotal
        })
        setNonSerialsSummaryData({
            saved: receivedNonSerialTotal,
            unexpected: unExpectedNonSerialTotal,
            missing: missingNonSerialTotal
        })
    }

    return (
        <Box p={4}>
            <Card sx={{width: '100%', margin: 'auto', padding: 2, boxShadow: 3}}>
                <CardContent>
                    <Typography variant="h4" align="center" sx={{marginBottom: 2}}>
                        Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="body1" align="center" sx={{fontSize: '1.25rem'}}>
                                <strong style={{fontWeight: 700, color: '#2e7d32'}}>Received
                                    Expected:</strong> {serialsSummaryData.saved} serials, {nonSerialsSummaryData.saved} non-serials
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body1" align="center" sx={{fontSize: '1.25rem'}}>
                                <strong style={{
                                    fontWeight: 700,
                                    color: '#ff7f00'
                                }}>Unexpected:</strong> {serialsSummaryData.unexpected} serials, {nonSerialsSummaryData.unexpected} non-serials
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body1" align="center" sx={{fontSize: '1.25rem'}}>
                                <strong style={{
                                    fontWeight: 700,
                                    color: '#cc0000'
                                }}>Missing:</strong> {serialsSummaryData.missing} serials, {nonSerialsSummaryData.missing} non-serials
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{padding: 2}}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="h6" color="#000000" sx={{marginBottom: 1, fontSize: '1.5rem'}}>Serials
                        Summary</Typography>
                    <Box sx={{height: 600, backgroundColor: '#f3f3f3'}}>
                        <ReceiveSummaryTable rows={serialReceivedRows} isSerial={true}/>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="h6" color="#000000" sx={{marginBottom: 1, fontSize: '1.5rem'}}>Non-Serials
                        Summary</Typography>
                    <Box sx={{height: 600, backgroundColor: '#f3f3f3'}}>
                        <ReceiveSummaryTable rows={nonSerialReceivedRows} isSerial={false}/>
                    </Box>
                </Grid>

            </Grid>

        </Box>
    )
}

export default ReceiveSummary
